import type { MusicImportCandidate, MusicImportJob } from "@needle-drop/shared";
import CloudDownload from "lucide-react/dist/esm/icons/cloud-download";
import Search from "lucide-react/dist/esm/icons/search";
import Youtube from "lucide-react/dist/esm/icons/youtube";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { addMusicImportCandidate, fetchCloudMusicImport, resolveMusicImport, startCloudMusicImport } from "../services/musicImport";
import { useLibraryStore } from "../store/libraryStore";
import { Dialog } from "./Dialog";

function duration(value: number) {
  if (!value) return "";
  const seconds = Math.round(value / 1_000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function jobIsActive(job?: MusicImportJob) {
  return job?.status === "queued" || job?.status === "running";
}

function jobStatus(job: MusicImportJob) {
  if (job.status === "completed") return "PLAYLIST READY IN PRIVATE CLOUD";
  if (job.status === "partial") return "PLAYLIST READY WITH SOME FAILED TRACKS";
  if (job.status === "failed") return "CLOUD IMPORT FAILED";
  return job.status === "queued" ? "CLOUD PROCESSOR IS STARTING" : "DOWNLOADING DIRECTLY TO PRIVATE CLOUD";
}

export function MusicImportDialog({ onClose }: { onClose(): void; onImport(files: File[]): void }) {
  const settings = useLibraryStore((state) => state.settings);
  const [input, setInput] = useState("");
  const [label, setLabel] = useState("");
  const [playlistName, setPlaylistName] = useState("Needle Drop Import");
  const [results, setResults] = useState<MusicImportCandidate[]>([]);
  const [job, setJob] = useState<MusicImportJob>();
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const controller = useRef<AbortController | undefined>(undefined);
  const syncedJobId = useRef<string | undefined>(undefined);

  useEffect(() => () => controller.current?.abort(), []);

  useEffect(() => {
    if (!job || !jobIsActive(job)) {
      if (job && job.id !== syncedJobId.current && ["completed", "partial"].includes(job.status)) {
        syncedJobId.current = job.id;
        void useLibraryStore.getState().syncLibrary(true);
      }
      return;
    }
    const timer = window.setTimeout(() => {
      fetchCloudMusicImport(settings.apiBaseUrl, job.id)
        .then(setJob)
        .catch((caught) => setError(caught instanceof Error ? caught.message : "Cloud import status could not be loaded."));
    }, 2_000);
    return () => window.clearTimeout(timer);
  }, [job, settings.apiBaseUrl]);

  async function saveCandidates(candidates: MusicImportCandidate[]) {
    const saved: MusicImportCandidate[] = [];
    for (const candidate of candidates) {
      const item = await addMusicImportCandidate(settings.apiBaseUrl, candidate);
      if (item) saved.push(item);
    }
    return saved;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    controller.current?.abort();
    const nextController = new AbortController();
    controller.current = nextController;
    setLoading(true);
    setError(undefined);
    setMessage(undefined);
    setJob(undefined);
    try {
      const response = await resolveMusicImport(settings.apiBaseUrl, value, nextController.signal);
      const saved = await saveCandidates(response.candidates);
      if (!saved.length) throw new Error("No usable YouTube matches were found.");
      setLabel(response.label);
      setPlaylistName(response.label.slice(0, 200) || "Needle Drop Import");
      setResults(saved);
      setMessage(response.warnings[0] ?? `${saved.length} official YouTube match${saved.length === 1 ? "" : "es"} ready for private cloud import.`);
    } catch (caught) {
      if (!nextController.signal.aborted) {
        setResults([]);
        setError(caught instanceof Error ? caught.message : "Music import lookup failed.");
      }
    } finally {
      if (!nextController.signal.aborted) setLoading(false);
    }
  }

  async function startImport() {
    if (!results.length) return;
    setStarting(true);
    setError(undefined);
    try {
      const next = await startCloudMusicImport(settings.apiBaseUrl, playlistName.trim() || label || "Needle Drop Import", results.map((item) => item.id));
      setJob(next);
      setMessage("Cloud import started. You can leave this window open to watch the progress.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Cloud import could not be started.");
    } finally {
      setStarting(false);
    }
  }

  function renderTrack(item: MusicImportCandidate) {
    const progress = job?.items.find((entry) => entry.candidateId === item.id);
    return <article className={`music-import-row ${progress?.status ?? ""}`} key={item.id}>
      {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <div className="spotify-art-placeholder">♪</div>}
      <div className="spotify-track-copy">
        <strong>{item.title}</strong>
        <span>{item.artist || "Unknown artist"}</span>
        <em>{[item.album, duration(item.durationMs), item.youtubeChannel].filter(Boolean).join(" · ")}</em>
        {progress?.error ? <small className="import-track-error">{progress.error}</small> : item.youtubeTitle && <small>{item.youtubeTitle}</small>}
      </div>
      <div className="spotify-result-actions">
        <a className="bevel-button spotify-open" href={item.youtubeUrl} target="_blank" rel="noreferrer"><Youtube size={11} /> TEST</a>
        {progress && <span className={`music-import-status ${progress.status}`}>{progress.status.toUpperCase()}</span>}
      </div>
    </article>;
  }

  const completed = job?.completed ?? 0;
  const failed = job?.failed ?? 0;
  const processed = completed + failed;
  const percent = job?.total ? Math.round((processed / job.total) * 100) : 0;

  return <Dialog title="MUSIC IMPORT" className="spotify-dialog" onClose={onClose}>
    <div className="music-import-layout">
      <section className="music-import-panel">
        <div className="spotify-section-title"><span>STEP 1: SPOTIFY LINK OR SONG</span><span>{results.length ? "RECOGNIZED" : "READY"}</span></div>
        <form className="spotify-search-form" onSubmit={(event) => void submit(event)}>
          <div className="spotify-search-input"><Search size={14} /><input autoFocus value={input} onChange={(event) => setInput(event.target.value)} placeholder="Song, artist, Spotify track, album, playlist or artist URL…" aria-label="Search or Spotify link" /></div>
          <button className="bevel-button primary-tool" disabled={loading || jobIsActive(job) || !input.trim()}>{loading ? "SEARCHING..." : "FIND"}</button>
        </form>
        <p className="spotify-note">Spotify tracks, albums, playlists and artists are expanded privately. Artist imports use the first 10 public top tracks.</p>
        {(message || error) && <p className={`spotify-message inline ${error ? "error" : ""}`}>{error ?? message}</p>}
      </section>

      <section className="spotify-search-panel">
        <div className="spotify-section-title"><span>{label ? `STEP 2: YOUTUBE MATCHES FOR ${label}` : "STEP 2: YOUTUBE MATCHES"}</span><span>{results.length} FOUND</span></div>
        <div className="spotify-results" aria-live="polite">
          {!loading && results.length === 0 && <p className="spotify-message">ENTER A SONG OR SPOTIFY LINK</p>}
          {results.map(renderTrack)}
        </div>
        <p className="spotify-note">TEST is optional and only opens the matched YouTube video for a quick check.</p>
      </section>

      <section className="youtube-links-panel">
        <div className="spotify-section-title"><span>STEP 3: PRIVATE CLOUD PLAYLIST</span><span>{job ? `${processed}/${job.total}` : `${results.length} READY`}</span></div>
        <div className="cloud-import-controls">
          <label>PLAYLIST NAME<input value={playlistName} maxLength={200} disabled={jobIsActive(job)} onChange={(event) => setPlaylistName(event.target.value)} /></label>
          <button className="bevel-button primary-tool" disabled={!results.length || starting || jobIsActive(job)} onClick={() => void startImport()}><CloudDownload size={16} /> {starting ? "STARTING CLOUD IMPORT..." : "IMPORT MP3S TO PRIVATE CLOUD"}</button>
        </div>
        {job && <div className={`cloud-import-progress ${job.status}`}>
          <div><strong>{jobStatus(job)}</strong><span>{completed} imported{failed ? ` · ${failed} failed` : ""}</span></div>
          <progress max={job.total} value={processed} aria-label={`${percent}% complete`} />
          <small>{percent}% · Playlist: {job.playlistName}</small>
          {job.error && <p>{job.error}</p>}
        </div>}
        <p className="spotify-note cloud-import-note">Audio is processed remotely with yt-dlp and FFmpeg, written directly to the private R2 library, and added to this playlist. Nothing is downloaded to this computer. Use only for audio you own or are allowed to download.</p>
      </section>
    </div>
  </Dialog>;
}
